/**
 * Mapping from membership roles to capability roles
 * Used when API fallback returns membership roles instead of capability roles
 * This should match the backend ROLE_PERMISSIONS structure
 */

export const MEMBERSHIP_ROLE_TO_CAPABILITIES: Record<string, string[]> = {
  owner: [
    // Organization
    "organization-view",
    "organization-create",
    "organization-update",
    "organization-delete",
    "organization-manage",
    // Workflow
    "workflow-view",
    "workflow-create",
    "workflow-update",
    "workflow-delete",
    "workflow-manage",
    "workflow-execute",
    "workflow-publish",
    // Campaign
    "campaign-view",
    "campaign-create",
    "campaign-update",
    "campaign-delete",
    "campaign-manage",
    "campaign-execute",
    // Channel
    "channel-view",
    "channel-create",
    "channel-update",
    "channel-delete",
    "channel-manage",
    "channel-configure",
    // CDP
    "cdp-view",
    "cdp-create",
    "cdp-update",
    "cdp-delete",
    "cdp-manage",
    "cdp-import",
    "cdp-export",
    // Conversation
    "conversation-view",
    "conversation-create",
    "conversation-update",
    "conversation-delete",
    "conversation-manage",
    // User
    "user-view",
    "user-create",
    "user-update",
    "user-delete",
    "user-manage",
    // Role
    "role-view",
    "role-create",
    "role-update",
    "role-delete",
    "role-manage",
    // Integration
    "integration-view",
    "integration-create",
    "integration-update",
    "integration-delete",
    "integration-manage",
    "integration-configure",
    // Billing
    "billing-view",
    "billing-update",
    "billing-manage",
    // Analytics
    "analytics-view",
    // API Key
    "apikey-view",
    "apikey-create",
    "apikey-update",
    "apikey-delete",
    // Webhook
    "webhook-view",
    "webhook-create",
    "webhook-update",
    "webhook-delete",
  ],
  admin: [
    // Organization (no delete)
    "organization-view",
    "organization-update",
    "organization-manage",
    // Workflow
    "workflow-view",
    "workflow-create",
    "workflow-update",
    "workflow-delete",
    "workflow-manage",
    "workflow-execute",
    "workflow-publish",
    // Campaign
    "campaign-view",
    "campaign-create",
    "campaign-update",
    "campaign-delete",
    "campaign-manage",
    "campaign-execute",
    // Channel
    "channel-view",
    "channel-create",
    "channel-update",
    "channel-delete",
    "channel-manage",
    "channel-configure",
    // CDP
    "cdp-view",
    "cdp-create",
    "cdp-update",
    "cdp-delete",
    "cdp-manage",
    "cdp-import",
    "cdp-export",
    // Conversation
    "conversation-view",
    "conversation-create",
    "conversation-update",
    "conversation-delete",
    "conversation-manage",
    // User
    "user-view",
    "user-create",
    "user-update",
    "user-delete",
    "user-manage",
    // Role
    "role-view",
    "role-create",
    "role-update",
    "role-delete",
    "role-manage",
    // Integration
    "integration-view",
    "integration-create",
    "integration-update",
    "integration-delete",
    "integration-manage",
    "integration-configure",
    // Billing
    "billing-view",
    "billing-update",
    "billing-manage",
    // Analytics
    "analytics-view",
    // API Key
    "apikey-view",
    "apikey-create",
    "apikey-update",
    "apikey-delete",
    // Webhook
    "webhook-view",
    "webhook-create",
    "webhook-update",
    "webhook-delete",
  ],
  supervisor: [
    // Organization
    "organization-view",
    // Workflow
    "workflow-view",
    "workflow-create",
    "workflow-update",
    "workflow-execute",
    // Campaign
    "campaign-view",
    "campaign-create",
    "campaign-update",
    "campaign-execute",
    // Channel
    "channel-view",
    "channel-create",
    "channel-update",
    // CDP
    "cdp-view",
    "cdp-create",
    "cdp-update",
    // Conversation
    "conversation-view",
    "conversation-update",
    // User
    "user-view",
    // Integration
    "integration-view",
    // Analytics
    "analytics-view",
  ],
  agent: [
    // Organization
    "organization-view",
    // Workflow
    "workflow-view",
    "workflow-execute",
    // Campaign
    "campaign-view",
    // Channel
    "channel-view",
    // CDP
    "cdp-view",
    // Conversation
    "conversation-view",
    "conversation-update",
    // Integration
    "integration-view",
    // Analytics
    "analytics-view",
  ],
  developer: [
    // Organization
    "organization-view",
    // Workflow
    "workflow-view",
    "workflow-create",
    "workflow-update",
    "workflow-delete",
    "workflow-execute",
    "workflow-publish",
    // Campaign
    "campaign-view",
    "campaign-create",
    "campaign-update",
    // Channel
    "channel-view",
    "channel-create",
    "channel-update",
    "channel-configure",
    // CDP
    "cdp-view",
    "cdp-create",
    "cdp-update",
    // Conversation
    "conversation-view",
    // User
    "user-view",
    // Integration
    "integration-view",
    "integration-create",
    "integration-update",
    "integration-configure",
    // Analytics
    "analytics-view",
    // API Key
    "apikey-view",
    "apikey-create",
    "apikey-update",
    // Webhook
    "webhook-view",
    "webhook-create",
    "webhook-update",
  ],
  marketing: [
    // Organization
    "organization-view",
    // Workflow
    "workflow-view",
    // Campaign
    "campaign-view",
    "campaign-create",
    "campaign-update",
    "campaign-delete",
    "campaign-execute",
    // Channel
    "channel-view",
    "channel-create",
    "channel-update",
    // CDP
    "cdp-view",
    "cdp-create",
    "cdp-update",
    // Conversation
    "conversation-view",
    // Integration
    "integration-view",
    // Analytics
    "analytics-view",
  ],
  reporter: [
    // Organization
    "organization-view",
    // Workflow
    "workflow-view",
    // Campaign
    "campaign-view",
    // Channel
    "channel-view",
    // CDP
    "cdp-view",
    // Conversation
    "conversation-view",
    // Integration
    "integration-view",
    // Analytics
    "analytics-view",
  ],
};

/**
 * Convert membership role to capability roles
 * Handles normalization of role names
 */
export function membershipRoleToCapabilities(membershipRole: string): string[] {
  // Normalize membership role
  const normalizedRole = membershipRole
    .toLowerCase()
    .replace(/^organization_/, "")
    .replace(/^org-/, "");

  const capabilities = MEMBERSHIP_ROLE_TO_CAPABILITIES[normalizedRole];
  
  if (!capabilities) {
    console.warn(`⚠️ Unknown membership role: ${membershipRole}, defaulting to reporter capabilities`);
    return MEMBERSHIP_ROLE_TO_CAPABILITIES.reporter || [];
  }

  return capabilities;
}


